import React, { useState, useEffect } from "react";
import { api } from "../../services/authService";

const NOTIFICATION_TYPES = [
  { key: "insufficientGrade", label: "Insufficient grades" },
  { key: "monthlyReport", label: "Monthly report" },
  { key: "semesterReport", label: "Semester report" },
];

const NotificationSettingsCard = () => {
  const [prefs, setPrefs] = useState(null);
  const [routing, setRouting] = useState([]);
  const [routingOpen, setRoutingOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [prefsRes, routingRes] = await Promise.all([
        api.get("/notification-preferences/me"),
        api.get("/notification-preferences/routing"),
      ]);
      setPrefs(prefsRes.data);
      setRouting(routingRes.data || []);
    } catch (err) {
      console.error("Failed to load notification settings", err);
      setError(true);
    }
  };

  const handleToggle = async (key) => {
    if (!prefs) return;

    const previous = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(key);

    try {
      const response = await api.put("/notification-preferences/me", {
        insufficientGrade: next.insufficientGrade,
        monthlyReport: next.monthlyReport,
        semesterReport: next.semesterReport,
      });
      setPrefs(response.data);
      const routingRes = await api.get("/notification-preferences/routing");
      setRouting(routingRes.data || []);
    } catch (err) {
      console.error("Failed to save notification settings", err);
      setPrefs(previous);
      setError(true);
      setTimeout(() => setError(false), 3000);
    } finally {
      setSaving(null);
    }
  };

  if (!prefs) return null;

  return (
    <div className="mb-6 rounded-xl border border-pale-dawn bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-semibold text-grey-dusk flex items-center gap-2 shrink-0">
          <i className="fas fa-bell text-[11px] text-sunrise-red"></i>
          My notifications
        </span>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {NOTIFICATION_TYPES.map((type) => {
            const enabled = !!prefs[type.key];
            return (
              <button
                key={type.key}
                type="button"
                onClick={() => handleToggle(type.key)}
                disabled={saving !== null}
                role="switch"
                aria-checked={enabled}
                title={
                  enabled
                    ? `You receive "${type.label}" emails`
                    : `You do not receive "${type.label}" emails`
                }
                className="inline-flex items-center gap-2 text-[11px] disabled:opacity-50 group"
              >
                <span
                  className={`w-8 h-[18px] rounded-full relative transition-colors shrink-0 ${
                    enabled
                      ? "bg-sunrise-red"
                      : "bg-pale-dawn group-hover:bg-slate-300"
                  }`}
                >
                  <span
                    className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-all flex items-center justify-center ${
                      enabled ? "left-[16px]" : "left-[2px]"
                    }`}
                  >
                    {saving === type.key && (
                      <i className="fas fa-circle-notch fa-spin text-[7px] text-slate-cloud"></i>
                    )}
                  </span>
                </span>
                <span
                  className={
                    enabled
                      ? "font-medium text-grey-dusk"
                      : "text-slate-cloud"
                  }
                >
                  {type.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-cloud">
          {error && (
            <span className="text-red-600">
              <i className="fas fa-exclamation-circle mr-1"></i>
              Could not save
            </span>
          )}
          {prefs.email && (
            <span
              className="hidden md:inline truncate max-w-[14rem]"
              title={
                prefs.configuredRecipient
                  ? "Your address is a configured default recipient — switch off what you don't want."
                  : undefined
              }
            >
              {prefs.email}
              {prefs.configuredRecipient && " · default recipient"}
            </span>
          )}
          <button
            type="button"
            onClick={() => setRoutingOpen((v) => !v)}
            className="underline decoration-dotted underline-offset-2 hover:text-grey-dusk"
          >
            {routingOpen ? "Hide recipients" : "Who gets what?"}
          </button>
        </div>
      </div>

      {routingOpen && (
        <div className="mt-3 pt-3 border-t border-pale-dawn overflow-x-auto">
          <table className="min-w-full text-[11px]">
            <thead>
              <tr className="text-slate-cloud">
                <th className="py-1 pr-4 text-left font-medium">Profession</th>
                <th className="py-1 pr-4 text-left font-medium">
                  Insufficient grades
                </th>
                <th className="py-1 text-left font-medium">Reports</th>
              </tr>
            </thead>
            <tbody>
              {routing.length > 0 ? (
                routing.map((row) => (
                  <tr key={row.professionType} className="align-top">
                    <td className="py-1 pr-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5 font-medium text-grey-dusk">
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: row.professionColor }}
                        ></span>
                        {row.professionLabel}
                      </span>
                    </td>
                    <td className="py-1 pr-4">
                      <RecipientCell
                        recipient={row.gradeRecipient}
                        recipientActive={row.gradeRecipientActive}
                        subscribers={row.gradeSubscribers}
                        ownEmail={prefs.email}
                      />
                    </td>
                    <td className="py-1">
                      <RecipientCell
                        recipient={row.reportRecipient}
                        recipientActive={row.reportRecipientActive}
                        subscribers={row.reportSubscribers}
                        ownEmail={prefs.email}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="py-2 text-slate-cloud">
                    No mail recipients configured for your professions.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RecipientCell = ({
  recipient,
  recipientActive = true,
  subscribers = [],
  ownEmail,
}) => (
  <div className="flex flex-col">
    {recipient ? (
      <span
        className={
          recipientActive
            ? "font-mono text-grey-dusk"
            : "font-mono text-slate-cloud line-through"
        }
        title={recipientActive ? undefined : "Opted out of these emails"}
      >
        {recipient}
        {!recipientActive && (
          <span className="ml-1 no-underline italic">(off)</span>
        )}
      </span>
    ) : (
      <span className="text-slate-cloud italic">not configured</span>
    )}

    {subscribers
      .filter((email) => email !== recipient)
      .map((email) => (
        <span
          key={email}
          className={`font-mono ${
            email === ownEmail
              ? "text-sunrise-red font-semibold"
              : "text-slate-cloud"
          }`}
        >
          + {email}
          {email === ownEmail && " (you)"}
        </span>
      ))}
  </div>
);

export default NotificationSettingsCard;
